/*
 * DORTI Tools: guitar tuner, chord identifier and scale identifier.
 * Loaded by sections/dorti-tool.liquid on the three tool pages and started
 * from the markup it finds ([data-dorti-tool]). Everything runs on the
 * visitor's device: the tuner's microphone audio is analysed here and never
 * leaves the browser.
 */
(function () {
  'use strict';

  if (window.__dortiTools) return;
  window.__dortiTools = true;

  /* ============================================================
     Music basics
  ============================================================ */

  var LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
  var LETTER_PC = [0, 2, 4, 5, 7, 9, 11];
  // How a pitch class is named when no chord or scale decides the spelling
  var PC_NAME = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
  var DEGREE_SEMIS = { 1: 0, 2: 2, 3: 4, 4: 5, 5: 7, 6: 9, 7: 11, 9: 2, 11: 5, 13: 9 };
  var ORDINAL = ['1st', '2nd', '3rd', '4th', '5th', '6th'];

  // Six strings, low to high, as MIDI notes
  var TUNINGS = {
    standard: [40, 45, 50, 55, 59, 64],
    dropD: [38, 45, 50, 55, 59, 64],
    halfDown: [39, 44, 49, 54, 58, 63],
    fullDown: [38, 43, 48, 53, 57, 62],
    dropC: [36, 43, 48, 53, 57, 62],
    openG: [38, 43, 50, 55, 59, 62],
    openD: [38, 45, 50, 54, 57, 62],
    dadgad: [38, 45, 50, 55, 57, 62]
  };
  // Tunings a player calls by flats name their strings with flats
  var FLAT_TUNINGS = { halfDown: true };

  function mod12(n) { return ((n % 12) + 12) % 12; }
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function freqOf(midi, a4) { return (a4 || 440) * Math.pow(2, (midi - 69) / 12); }
  function octaveOf(midi) { return Math.floor(midi / 12) - 1; }

  function pcName(pc, flats) {
    var n = PC_NAME[mod12(pc)];
    if (flats && n.indexOf('♯') > 0) {
      var l = LETTERS[(LETTERS.indexOf(n[0]) + 1) % 7];
      return l + '♭';
    }
    return n;
  }

  function accStr(a) {
    if (a === 0) return '';
    if (a === 1) return '♯';
    if (a === -1) return '♭';
    return a > 1 ? '𝄪' : '𝄫';
  }

  // "b3" / "#11" / "bb7" -> { semis, degree, label }
  function parseDegree(tok) {
    var m = /^([b#]*)(\d+)$/.exec(tok);
    var acc = m[1];
    var deg = +m[2];
    var shift = (acc.match(/#/g) || []).length - (acc.match(/b/g) || []).length;
    return {
      semis: mod12(DEGREE_SEMIS[deg] + shift),
      degree: deg,
      label: tok.replace(/b/g, '♭').replace(/#/g, '♯')
    };
  }

  function parseFormula(f) { return f.split(' ').map(parseDegree); }

  // Spell every degree from a root spelling. Fewer accidentals = cheaper.
  function spellFrom(letter, acc, degs) {
    var rootPc = mod12(LETTER_PC[letter] + acc);
    var cost = Math.abs(acc);
    var names = degs.map(function (d) {
      var l = (letter + ((d.degree - 1) % 7)) % 7;
      var pc = mod12(rootPc + d.semis);
      var a = mod12(pc - LETTER_PC[l]);
      if (a > 6) a -= 12;
      cost += Math.abs(a) + (Math.abs(a) > 1 ? 8 : 0);
      return LETTERS[l] + accStr(a);
    });
    return { root: LETTERS[letter] + accStr(acc), names: names, cost: cost };
  }

  // Best spelling of a chord or scale on a root pitch class
  function spell(rootPc, degs) {
    var best = null;
    for (var l = 0; l < 7; l++) {
      var a = mod12(rootPc - LETTER_PC[l]);
      if (a > 6) a -= 12;
      if (Math.abs(a) > 1) continue;
      // A natural root name always wins over B♯ / F♭ style names
      if (a !== 0 && LETTER_PC.indexOf(mod12(rootPc)) >= 0) continue;
      var s = spellFrom(l, a, degs);
      if (!best || s.cost < best.cost || (s.cost === best.cost && s.root === PC_NAME[mod12(rootPc)])) best = s;
    }
    return best;
  }

  // Render text with ♭/♯ wrapped so they sit right in Anton
  function accHTML(s) {
    return String(s).replace(/[♭♯𝄪𝄫]/g, function (c) { return '<span class="dt-acc">' + c + '</span>'; });
  }

  function esc(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* ============================================================
     Sound: one shared context, Karplus-Strong plucked strings
  ============================================================ */

  var Sound = {
    _ctx: null,
    _out: null,
    _cache: {},

    ctx: function () {
      if (!this._ctx) {
        var C = window.AudioContext || window.webkitAudioContext;
        if (!C) return null;
        this._ctx = new C();
        this._out = this._ctx.createGain();
        this._out.gain.value = 0.85;
        this._out.connect(this._ctx.destination);
      }
      if (this._ctx.state === 'suspended') this._ctx.resume();
      return this._ctx;
    },

    // A plucked string at an exact pitch: the loop delay is fractional
    // (linear interpolation) so the reference tones are in tune.
    buffer: function (freq, dur) {
      var ctx = this.ctx();
      var key = freq.toFixed(2) + '|' + dur;
      if (this._cache[key]) return this._cache[key];
      var sr = ctx.sampleRate;
      var len = Math.floor(sr * dur);
      var b = ctx.createBuffer(1, len, sr);
      var y = b.getChannelData(0);
      var d = sr / freq - 0.5;
      var di = Math.floor(d);
      var fr = d - di;
      var burst = Math.ceil(d);
      var decay = freq < 150 ? 0.9986 : freq < 400 ? 0.9972 : 0.9955;
      var seed = 9001;
      var lp = 0;
      var i;
      for (i = 0; i < len; i++) {
        var exc = 0;
        if (i < burst) {
          seed = (seed * 1103515245 + 12345) & 0x7fffffff;
          lp = 0.55 * lp + 0.45 * ((seed / 0x7fffffff) * 2 - 1);
          exc = lp;
        }
        var a = i - di;
        var ya = a >= 0 ? y[a] : 0;
        var yb = a >= 1 ? y[a - 1] : 0;
        var yc = a >= 2 ? y[a - 2] : 0;
        y[i] = exc + decay * 0.5 * ((ya * (1 - fr) + yb * fr) + (yb * (1 - fr) + yc * fr));
      }
      var peak = 0;
      for (i = 0; i < len; i++) if (Math.abs(y[i]) > peak) peak = Math.abs(y[i]);
      if (peak > 0) for (i = 0; i < len; i++) y[i] *= 0.5 / peak;
      var fade = Math.floor(sr * 0.3);
      for (i = 0; i < fade; i++) y[len - 1 - i] *= i / fade;
      this._cache[key] = b;
      return b;
    },

    pluck: function (freq, at, dur, gain) {
      var ctx = this.ctx();
      if (!ctx) return;
      var src = ctx.createBufferSource();
      src.buffer = this.buffer(freq, dur || 2.6);
      var g = ctx.createGain();
      g.gain.value = gain == null ? 1 : gain;
      src.connect(g);
      g.connect(this._out);
      src.start(at || ctx.currentTime);
    },

    strum: function (freqs) {
      var ctx = this.ctx();
      if (!ctx) return;
      var t = ctx.currentTime + 0.03;
      var self = this;
      freqs.forEach(function (f, i) { self.pluck(f, t + i * 0.04, 3, 0.62); });
    },

    run: function (freqs, step) {
      var ctx = this.ctx();
      if (!ctx) return;
      var t = ctx.currentTime + 0.03;
      var self = this;
      freqs.forEach(function (f, i) { self.pluck(f, t + i * (step || 0.28), 1.4, 0.8); });
    }
  };

  /* ============================================================
     Pitch detection: McLeod Pitch Method, autocorrelation by FFT
  ============================================================ */

  function makeFFT(N) {
    var levels = Math.round(Math.log(N) / Math.LN2);
    var cos = new Float32Array(N / 2);
    var sin = new Float32Array(N / 2);
    var rev = new Uint32Array(N);
    var i;
    for (i = 0; i < N / 2; i++) {
      cos[i] = Math.cos((2 * Math.PI * i) / N);
      sin[i] = Math.sin((2 * Math.PI * i) / N);
    }
    for (i = 0; i < N; i++) {
      var r = 0;
      var x = i;
      for (var b = 0; b < levels; b++) { r = (r << 1) | (x & 1); x >>= 1; }
      rev[i] = r;
    }
    return function (re, im) {
      var j, t;
      for (var p = 0; p < N; p++) {
        j = rev[p];
        if (j > p) {
          t = re[p]; re[p] = re[j]; re[j] = t;
          t = im[p]; im[p] = im[j]; im[j] = t;
        }
      }
      for (var size = 2; size <= N; size <<= 1) {
        var half = size >> 1;
        var step = N / size;
        for (var s = 0; s < N; s += size) {
          for (var q = 0, k = 0; q < half; q++, k += step) {
            var a = s + q;
            var bb = a + half;
            var tre = re[bb] * cos[k] + im[bb] * sin[k];
            var tim = im[bb] * cos[k] - re[bb] * sin[k];
            re[bb] = re[a] - tre;
            im[bb] = im[a] - tim;
            re[a] += tre;
            im[a] += tim;
          }
        }
      }
    };
  }

  function PitchDetector(n) {
    var N = 1;
    while (N < n * 2) N <<= 1;
    this.n = n;
    this.N = N;
    this.re = new Float32Array(N);
    this.im = new Float32Array(N);
    this.sq = new Float64Array(n + 1);
    this.ns = new Float32Array(n + 1);
    this.fft = makeFFT(N);
  }

  // Returns { freq, clarity } or null when there is no clear note
  PitchDetector.prototype.detect = function (x, sr) {
    var n = this.n;
    var N = this.N;
    var re = this.re;
    var im = this.im;
    var sq = this.sq;
    var ns = this.ns;
    var i;
    var mean = 0;
    for (i = 0; i < n; i++) mean += x[i];
    mean /= n;
    var energy = 0;
    sq[0] = 0;
    for (i = 0; i < n; i++) {
      var v = x[i] - mean;
      re[i] = v;
      energy += v * v;
      sq[i + 1] = energy;
    }
    if (Math.sqrt(energy / n) < 0.0035) return null;
    for (i = n; i < N; i++) re[i] = 0;
    im.fill(0);

    this.fft(re, im);
    for (i = 0; i < N; i++) { re[i] = re[i] * re[i] + im[i] * im[i]; im[i] = 0; }
    // The power spectrum is real and even, so a forward transform is N x the inverse
    this.fft(re, im);

    var maxTau = Math.min(n - 2, Math.floor(sr / 50));
    var minTau = Math.max(2, Math.floor(sr / 1400));
    for (var tau = 0; tau <= maxTau + 1; tau++) {
      var m = sq[n - tau] + (sq[n] - sq[tau]);
      ns[tau] = m > 0 ? (2 * re[tau]) / N / m : 0;
    }

    // Key maxima: the highest point of each positive lobe after the first
    var peaks = [];
    var inLobe = false;
    var best = -Infinity;
    var bestTau = -1;
    for (tau = 1; tau <= maxTau; tau++) {
      if (!inLobe && ns[tau - 1] <= 0 && ns[tau] > 0) {
        inLobe = true;
        best = -Infinity;
        bestTau = -1;
      } else if (inLobe && ns[tau] <= 0) {
        inLobe = false;
        if (bestTau > 0) peaks.push(bestTau);
      }
      if (inLobe && tau >= minTau && ns[tau] > best) { best = ns[tau]; bestTau = tau; }
    }
    if (inLobe && bestTau > 0 && bestTau < maxTau) peaks.push(bestTau);
    if (!peaks.length) return null;

    var top = 0;
    for (i = 0; i < peaks.length; i++) if (ns[peaks[i]] > top) top = ns[peaks[i]];
    var chosen = -1;
    for (i = 0; i < peaks.length; i++) {
      if (ns[peaks[i]] >= 0.9 * top) { chosen = peaks[i]; break; }
    }
    if (chosen < 1 || ns[chosen] < 0.62) return null;

    var a0 = ns[chosen - 1];
    var b0 = ns[chosen];
    var c0 = ns[chosen + 1];
    var den = a0 - 2 * b0 + c0;
    var shift = den !== 0 ? (0.5 * (a0 - c0)) / den : 0;
    return { freq: sr / (chosen + clamp(shift, -1, 1)), clarity: b0 };
  };

  /* ============================================================
     TUNER
  ============================================================ */

  function Tuner(root) {
    var $ = function (s) { return root.querySelector(s); };
    var pedal = $('[data-pedal]');
    var face = $('[data-face]');
    var needle = $('[data-needle]');
    var noteEl = $('[data-note]');
    var hzEl = $('[data-hz]');
    var centsEl = $('[data-cents]');
    var whereEl = $('[data-where]');
    var dirEl = $('[data-direction]');
    var startBtn = $('[data-start]');
    var startLabel = $('[data-start-label]');
    var micLabel = $('[data-mic-label]');
    var errEl = $('[data-error]');
    var stringsEl = $('[data-strings]');
    var tuningSel = $('[data-tuning]');
    var a4Out = $('[data-a4-value]');
    var modeBtns = root.querySelectorAll('[data-mode]');

    var mode = 'auto';
    var tuning = 'standard';
    var a4 = 440;
    var target = 0;
    var running = false;
    var stream = null;
    var srcNode = null;
    var analyser = null;
    var sink = null;
    var detector = null;
    var buf = null;
    var raf = 0;
    var lastRun = 0;
    var lastHeard = 0;
    var lockSince = 0;
    var hist = [];
    var histKey = '';
    var shown = 0;
    var want = 0;
    var lastUi = {};

    function strings() {
      var flats = FLAT_TUNINGS[tuning];
      return TUNINGS[tuning].map(function (midi, i) {
        return { i: i, midi: midi, freq: freqOf(midi, a4), name: pcName(midi, flats), octave: octaveOf(midi) };
      });
    }

    function renderStrings() {
      stringsEl.innerHTML = strings().map(function (s) {
        return '<button type="button" class="dt-string" data-string="' + s.i + '" aria-label="' +
          esc(ORDINAL[5 - s.i] + ' string, ' + s.name + s.octave + '. Play reference tone') + '">' +
          '<span class="dt-string__note">' + accHTML(s.name) + '</span>' +
          '<span class="dt-string__num">' + (6 - s.i) + ' · ' + s.octave + '</span></button>';
      }).join('');
      lastUi = {};
      markStrings(mode === 'string' ? target : -1, false);
    }

    function markStrings(idx, locked) {
      if (lastUi.idx === idx && lastUi.locked === locked) return;
      lastUi.idx = idx;
      lastUi.locked = locked;
      var btns = stringsEl.querySelectorAll('.dt-string');
      for (var i = 0; i < btns.length; i++) {
        btns[i].classList.toggle('is-target', i === idx);
        btns[i].classList.toggle('is-locked', i === idx && locked);
        btns[i].setAttribute('aria-pressed', i === idx ? 'true' : 'false');
      }
    }

    function setMode(m) {
      mode = m;
      for (var i = 0; i < modeBtns.length; i++) {
        modeBtns[i].setAttribute('aria-checked', modeBtns[i].getAttribute('data-mode') === m ? 'true' : 'false');
      }
      hist = [];
      lastUi = {};
      markStrings(m === 'string' ? target : -1, false);
      if (!running) idleText();
    }

    function idleText() {
      face.classList.add('is-idle');
      face.classList.remove('is-locked');
      face.removeAttribute('data-dir');
      needle.style.transform = 'rotate(0deg)';
      noteEl.innerHTML = mode === 'string' ? noteHTML(strings()[target]) : '–';
      hzEl.textContent = '– Hz';
      centsEl.textContent = '– ¢';
      whereEl.textContent = mode === 'string' ? ORDINAL[5 - target] + ' string' : 'Any string';
      dirEl.textContent = 'Start, then pluck a string';
    }

    function noteHTML(s) { return accHTML(s.name) + '<sub>' + s.octave + '</sub>'; }

    function pickTarget(f) {
      var list = strings();
      if (mode === 'string') {
        var s = list[target];
        return { key: 's' + target, s: s, idx: target };
      }
      var best = null;
      var bestAbs = Infinity;
      list.forEach(function (st) {
        var c = Math.abs(1200 * Math.log2(f / st.freq));
        if (c < bestAbs) { bestAbs = c; best = st; }
      });
      if (bestAbs <= 260) return { key: 's' + best.i, s: best, idx: best.i };
      var midi = Math.round(69 + 12 * Math.log2(f / a4));
      return {
        key: 'c' + midi,
        s: { midi: midi, freq: freqOf(midi, a4), name: pcName(midi, FLAT_TUNINGS[tuning]), octave: octaveOf(midi) },
        idx: -1
      };
    }

    function median(arr) {
      var s = arr.slice().sort(function (a, b) { return a - b; });
      return s[s.length >> 1];
    }

    function analyse(t) {
      analyser.getFloatTimeDomainData(buf);
      var r = detector.detect(buf, srcNode.context.sampleRate);
      if (!r || r.freq < 55 || r.freq > 1400) {
        if (t - lastHeard > 1400 && lastUi.state !== 'listening') {
          lastUi.state = 'listening';
          face.classList.remove('is-locked');
          face.removeAttribute('data-dir');
          want = 0;
          lockSince = 0;
          dirEl.textContent = mode === 'string' ? 'Pluck the ' + ORDINAL[5 - target] + ' string' : 'Listening… pluck a string';
          hzEl.textContent = '– Hz';
          centsEl.textContent = '– ¢';
        }
        return;
      }
      lastHeard = t;
      lastUi.state = 'note';
      var tg = pickTarget(r.freq);
      var cents = 1200 * Math.log2(r.freq / tg.s.freq);
      // A harmonic an octave up still means the same string
      if (Math.abs(cents) > 600) cents -= 1200 * Math.round(cents / 1200);
      if (tg.key !== histKey) { hist = []; histKey = tg.key; lockSince = 0; }
      hist.push(cents);
      if (hist.length > 5) hist.shift();
      var c = median(hist);
      want = c;

      var abs = Math.abs(c);
      var dir = abs <= 3 ? 'in' : abs <= 10 ? 'close' : c < 0 ? 'flat' : 'sharp';
      if (dir === 'in') { if (!lockSince) lockSince = t; } else lockSince = 0;
      var locked = dir === 'in' && t - lockSince > 320;

      var noteKey = tg.key;
      if (lastUi.note !== noteKey) { noteEl.innerHTML = noteHTML(tg.s); lastUi.note = noteKey; }
      whereEl.textContent = tg.idx >= 0 ? ORDINAL[5 - tg.idx] + ' string' : 'Chromatic';
      hzEl.textContent = r.freq.toFixed(1) + ' Hz';
      var rc = Math.round(c);
      centsEl.textContent = (rc > 0 ? '+' : rc < 0 ? '−' : '') + Math.abs(rc) + ' ¢';
      var text = locked ? 'In tune' :
        dir === 'in' || dir === 'close' ? (c < 0 ? 'Almost · up a hair' : c > 0 ? 'Almost · down a hair' : 'Almost') :
        dir === 'flat' ? 'Tune up ↑' : 'Tune down ↓';
      if (lastUi.text !== text) { dirEl.textContent = text; lastUi.text = text; }
      face.setAttribute('data-dir', locked ? 'in' : dir);
      face.classList.toggle('is-locked', locked);
      markStrings(tg.idx, locked);
    }

    function frame(t) {
      raf = requestAnimationFrame(frame);
      if (t - lastRun >= 30) { lastRun = t; analyse(t); }
      shown += (want - shown) * 0.22;
      needle.style.transform = 'rotate(' + (clamp(shown, -50, 50) * 1.3).toFixed(2) + 'deg)';
    }

    function setRunning(on) {
      running = on;
      pedal.setAttribute('data-state', on ? 'listening' : 'idle');
      startLabel.textContent = on ? 'Stop tuner' : 'Start tuner';
      startBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
      micLabel.textContent = on ? 'Mic on' : 'Mic off';
    }

    function fail(msg) {
      pedal.setAttribute('data-state', 'error');
      micLabel.textContent = 'Mic blocked';
      errEl.textContent = msg;
      errEl.hidden = false;
    }

    function errorText(err) {
      var n = err && err.name;
      if (n === 'NotAllowedError' || n === 'SecurityError' || n === 'PermissionDeniedError') {
        return 'Microphone access is blocked. Allow it in your browser’s site settings, then tap Start again. You can still tune by ear: tap a string below to hear its note.';
      }
      if (n === 'NotFoundError' || n === 'DevicesNotFoundError') {
        return 'No microphone found. Plug one in, or tune by ear: tap a string below to hear its note.';
      }
      if (n === 'NotReadableError' || n === 'TrackStartError') {
        return 'Another app is using your microphone. Close it and tap Start again.';
      }
      return 'The microphone didn’t start. Tap Start to try again.';
    }

    function release() {
      cancelAnimationFrame(raf);
      raf = 0;
      if (stream) stream.getTracks().forEach(function (tr) { tr.stop(); });
      if (srcNode) srcNode.disconnect();
      if (analyser) analyser.disconnect();
      if (sink) sink.disconnect();
      stream = srcNode = analyser = sink = null;
    }

    function start() {
      errEl.hidden = true;
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        fail('This browser can’t use the microphone here. Try Chrome, Safari or Firefox, or tune by ear: tap a string below to hear its note.');
        return;
      }
      // Created inside the tap, so iOS lets it make sound and hear the mic
      var ctx = Sound.ctx();
      if (!ctx) { fail('This browser has no Web Audio support.'); return; }
      startLabel.textContent = 'Starting…';
      navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
      }).then(function (s) {
        stream = s;
        return ctx.resume();
      }).then(function () {
        srcNode = ctx.createMediaStreamSource(stream);
        analyser = ctx.createAnalyser();
        analyser.fftSize = 4096;
        // Safari only runs nodes that lead to the speakers; a silent gain keeps it pulling
        sink = ctx.createGain();
        sink.gain.value = 0;
        srcNode.connect(analyser);
        analyser.connect(sink);
        sink.connect(ctx.destination);
        buf = new Float32Array(analyser.fftSize);
        if (!detector || detector.n !== analyser.fftSize) detector = new PitchDetector(analyser.fftSize);
        hist = [];
        histKey = '';
        lockSince = 0;
        lastHeard = performance.now();
        lastUi = {};
        face.classList.remove('is-idle');
        setRunning(true);
        dirEl.textContent = 'Listening… pluck a string';
        raf = requestAnimationFrame(frame);
      }).catch(function (err) {
        release();
        setRunning(false);
        fail(errorText(err));
      });
    }

    function stop() {
      release();
      setRunning(false);
      want = shown = 0;
      lastUi = {};
      markStrings(mode === 'string' ? target : -1, false);
      idleText();
    }

    startBtn.addEventListener('click', function () { if (running) stop(); else start(); });

    for (var i = 0; i < modeBtns.length; i++) {
      modeBtns[i].addEventListener('click', function () { setMode(this.getAttribute('data-mode')); });
    }

    stringsEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-string]');
      if (!b) return;
      var idx = +b.getAttribute('data-string');
      var s = strings()[idx];
      Sound.pluck(s.freq, 0, 3.4, 0.9);
      b.classList.remove('is-ringing');
      void b.offsetWidth;
      b.classList.add('is-ringing');
      if (mode === 'string' || !running) {
        target = idx;
        if (mode !== 'string') setMode('string');
        else { lastUi = {}; markStrings(idx, false); }
        if (!running) idleText();
      }
    });

    tuningSel.addEventListener('change', function () {
      tuning = TUNINGS[tuningSel.value] ? tuningSel.value : 'standard';
      target = 0;
      renderStrings();
      if (!running) idleText();
    });

    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-a4]');
      if (!b) return;
      a4 = clamp(a4 + +b.getAttribute('data-a4'), 430, 450);
      a4Out.textContent = a4;
      lastUi = {};
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden && running) stop();
    });

    renderStrings();
    idleText();
  }

  /* ============================================================
     Theory: chords and scales
  ============================================================ */

  // [symbol, name, formula, how common (ranking), degrees that may be left out]
  var CHORD_DEFS = [
    ['', 'major', '1 3 5', 100],
    ['m', 'minor', '1 b3 5', 98],
    ['7', 'dominant seventh', '1 3 5 b7', 92, '5'],
    ['m7', 'minor seventh', '1 b3 5 b7', 91, '5'],
    ['maj7', 'major seventh', '1 3 5 7', 90, '5'],
    ['5', 'power chord', '1 5', 86],
    ['sus4', 'suspended fourth', '1 4 5', 84],
    ['sus2', 'suspended second', '1 2 5', 82],
    ['add9', 'added ninth', '1 3 5 9', 80],
    ['dim', 'diminished', '1 b3 b5', 78],
    ['6', 'major sixth', '1 3 5 6', 76, '5'],
    ['m7♭5', 'half-diminished', '1 b3 b5 b7', 76],
    ['dim7', 'diminished seventh', '1 b3 b5 bb7', 75],
    ['madd9', 'minor added ninth', '1 b3 5 9', 74],
    ['aug', 'augmented', '1 3 #5', 74],
    ['9', 'dominant ninth', '1 3 5 b7 9', 74, '5'],
    ['7sus4', 'seventh suspended fourth', '1 4 5 b7', 73, '5'],
    ['m6', 'minor sixth', '1 b3 5 6', 72, '5'],
    ['m9', 'minor ninth', '1 b3 5 b7 9', 70, '5'],
    ['maj9', 'major ninth', '1 3 5 7 9', 70, '5'],
    ['7♯9', 'seventh sharp nine', '1 3 5 b7 #9', 66, '5'],
    ['6/9', 'six-nine', '1 3 5 6 9', 64, '5'],
    ['7♭9', 'seventh flat nine', '1 3 5 b7 b9', 62, '5'],
    ['m(maj7)', 'minor major seventh', '1 b3 5 7', 60, '5'],
    ['7♭5', 'seventh flat five', '1 3 b5 b7', 58],
    ['aug7', 'augmented seventh', '1 3 #5 b7', 58],
    ['13', 'dominant thirteenth', '1 3 5 b7 9 13', 54, '5 9'],
    ['add11', 'added eleventh', '1 3 5 11', 52],
    ['9sus4', 'ninth suspended fourth', '1 4 5 b7 9', 52, '5'],
    ['m11', 'minor eleventh', '1 b3 5 b7 9 11', 52, '5 9'],
    ['7sus2', 'seventh suspended second', '1 2 5 b7', 50, '5'],
    ['maj7♯5', 'augmented major seventh', '1 3 #5 7', 50],
    ['11', 'dominant eleventh', '1 3 5 b7 9 11', 48, '3 5 9'],
    ['m13', 'minor thirteenth', '1 b3 5 b7 9 13', 46, '5 9'],
    ['maj13', 'major thirteenth', '1 3 5 7 9 13', 46, '5 9'],
    ['maj7♯11', 'major seventh sharp eleven', '1 3 5 7 #11', 44, '5']
  ];

  var CHORDS = CHORD_DEFS.map(function (d, i) {
    var degs = parseFormula(d[2]);
    var opt = (d[4] || '').split(' ').filter(Boolean).map(Number);
    var all = {};
    var req = {};
    degs.forEach(function (g) {
      all[g.semis] = g;
      if (opt.indexOf(g.degree) < 0) req[g.semis] = true;
    });
    return { id: i, sym: d[0], name: d[1], degs: degs, w: d[3], all: all, req: req, size: degs.length };
  });

  // Chord types offered in "Build a chord", in menu order
  var BUILD_TYPES = ['', 'm', '7', 'm7', 'maj7', '5', 'sus2', 'sus4', 'add9', '6', 'm6', '9', 'dim', 'dim7', 'm7♭5', 'aug', '7sus4', 'm9', 'maj9'];

  function chordBySym(sym) {
    for (var i = 0; i < CHORDS.length; i++) if (CHORDS[i].sym === sym) return CHORDS[i];
    return CHORDS[0];
  }

  function chordFits(rel, t) {
    for (var i = 0; i < rel.length; i++) if (!t.all[rel[i]]) return false;
    return true;
  }

  // Every exact name for a set of pitch classes, best first
  function identifyChord(pcs, bassPc) {
    var out = [];
    pcs.forEach(function (root) {
      var rel = pcs.map(function (p) { return mod12(p - root); });
      CHORDS.forEach(function (t) {
        if (!chordFits(rel, t)) return;
        var missing = 0;
        for (var s in t.all) if (rel.indexOf(+s) < 0) { if (t.req[s]) return; missing++; }
        var score = t.w - missing * 6;
        if (root === bassPc) score += 30;
        else {
          var bi = mod12(bassPc - root);
          score -= bi === 3 || bi === 4 || bi === 7 ? 4 : 10;
        }
        out.push({ root: root, type: t, bass: bassPc, score: score, missing: missing });
      });
    });
    out.sort(function (a, b) { return b.score - a.score; });
    return out;
  }

  // Chords these notes could grow into (for one or two notes, or no exact match)
  function chordCandidates(pcs, bassPc) {
    var out = [];
    for (var root = 0; root < 12; root++) {
      var rel = pcs.map(function (p) { return mod12(p - root); });
      CHORDS.forEach(function (t) {
        if (t.w < 64 || t.size < 3) return;
        if (!chordFits(rel, t)) return;
        var need = 0;
        for (var s in t.req) if (rel.indexOf(+s) < 0) need++;
        if (!need) return;
        var score = t.w - need * 8 + (pcs.indexOf(root) >= 0 ? 10 : 0) + (root === bassPc ? 14 : 0);
        out.push({ root: root, type: t, score: score, need: need });
      });
    }
    out.sort(function (a, b) { return b.score - a.score; });
    return out.slice(0, 10);
  }

  function chordName(root, type, bassPc) {
    var sp = spell(root, type.degs);
    var name = sp.root + type.sym;
    var bassName = null;
    if (bassPc != null && bassPc !== root) {
      type.degs.forEach(function (d, i) { if (mod12(root + d.semis) === bassPc) bassName = sp.names[i]; });
      if (!bassName) bassName = PC_NAME[bassPc];
      name += '/' + bassName;
    }
    var full = sp.root + ' ' + type.name + (bassName ? ', ' + bassName + ' in the bass' : '');
    return { name: name, full: full, spelling: sp };
  }

  /*
   * Playable shapes for a chord. Slides a four-fret window up the neck and
   * tries every mix of chord tones, open strings and muted strings; keeps the
   * shapes a hand can actually make (four fingers, one barre, no gaps in
   * the middle of a strum).
   *   fixed: per string, a fret the shape must keep, 'x' to keep muted, or
   *          undefined when the string is free.
   */
  function findShapes(root, type, tuning, opts) {
    opts = opts || {};
    var fixed = opts.fixed || [];
    var maxFret = opts.maxFret || 15;
    var wantBass = opts.bass == null ? root : opts.bass;
    var all = type.all;
    var reqList = Object.keys(type.req).map(Number);
    var seen = {};
    var found = [];

    for (var w = 0; w <= maxFret - 3; w++) {
      var lo = Math.max(1, w);
      var hi = Math.min(maxFret, w + 3);
      var ok = true;
      var options = [];
      for (var s = 0; s < 6; s++) {
        var fx = fixed[s];
        if (fx !== undefined) {
          if (typeof fx === 'number' && fx > 0 && (fx < lo || fx > hi)) ok = false;
          options.push([fx]);
          continue;
        }
        var o = ['x'];
        if (w <= 3 && all[mod12(tuning[s] - root)]) o.push(0);
        for (var f = lo; f <= hi; f++) if (all[mod12(tuning[s] + f - root)]) o.push(f);
        options.push(o);
      }
      if (!ok) continue;

      var v = [];
      (function rec(si, bassSet) {
        if (si === 6) { consider(v.slice()); return; }
        var list = options[si];
        for (var k = 0; k < list.length; k++) {
          var fr = list[k];
          var nowBass = bassSet;
          if (fr !== 'x' && !bassSet) {
            if (fixed[si] === undefined && mod12(tuning[si] + fr) !== wantBass && !opts.anyBass) continue;
            nowBass = true;
          }
          v[si] = fr;
          rec(si + 1, nowBass);
        }
      })(0, false);
    }

    function consider(sh) {
      var first = -1;
      var last = -1;
      var sounding = 0;
      var have = {};
      var minF = 99;
      var maxF = 0;
      var open = 0;
      for (var s = 0; s < 6; s++) {
        var f = sh[s];
        if (f === 'x') continue;
        if (first < 0) first = s;
        last = s;
        sounding++;
        have[mod12(tuning[s] + f - root)] = true;
        if (f === 0) open++;
        else { if (f < minF) minF = f; if (f > maxF) maxF = f; }
      }
      var minStrings = Math.min(type.size, 3);
      if (sounding < minStrings) return;
      for (var r = 0; r < reqList.length; r++) if (!have[reqList[r]]) return;
      if (maxF && maxF - minF > 3) return;
      var gaps = 0;
      for (s = first; s <= last; s++) if (sh[s] === 'x') gaps++;
      if (gaps > 1) return;
      // Fingers: a barre covers every note on the lowest fret
      var fingers = 0;
      var atMin = 0;
      for (s = 0; s < 6; s++) {
        if (sh[s] === 'x' || sh[s] === 0) continue;
        if (sh[s] === minF) atMin++;
        else fingers++;
      }
      fingers += atMin > 0 ? (atMin > 1 && open === 0 ? 1 : atMin) : 0;
      if (fingers > 4) return;
      var key = sh.join(',');
      if (seen[key]) return;
      seen[key] = true;
      var optHave = 0;
      for (var t in all) if (have[t] && !type.req[t]) optHave++;
      var span = maxF ? maxF - minF : 0;
      var score = sounding * 10 - gaps * 26 + optHave * 4 + (sh[5] === 'x' ? -6 : 0) -
        (maxF ? minF * 1.2 : 0) + (minF === 99 || minF <= 3 ? open * 3 : -open * 4) -
        Math.max(0, span - 2) * 8 - fingers * 1.5 -
        // Open strings ringing under a hand way up the neck: rarely a real shape
        (open && maxF > 3 ? 12 : 0);
      found.push({ shape: sh, score: score, pos: maxF ? minF : 0 });
    }

    found.sort(function (a, b) { return b.score - a.score; });
    if (!opts.spread) return found.slice(0, opts.limit || 1);
    // The best shape in each area of the neck (two frets apart), then low to high
    var byPos = [];
    found.forEach(function (x) {
      if (byPos.length >= (opts.limit || 6)) return;
      // Awkward stretches and gappy shapes only when nothing better exists
      if (x.score < 12 && byPos.length) return;
      for (var i = 0; i < byPos.length; i++) if (Math.abs(byPos[i].pos - x.pos) < 2) return;
      byPos.push(x);
    });
    byPos.sort(function (a, b) { return a.pos - b.pos; });
    return byPos;
  }

  // [name, formula, how common, also known as]
  var SCALE_DEFS = [
    ['major', '1 2 3 4 5 6 7', 100, 'Ionian'],
    ['natural minor', '1 2 b3 4 5 b6 b7', 99, 'Aeolian'],
    ['minor pentatonic', '1 b3 4 5 b7', 98],
    ['major pentatonic', '1 2 3 5 6', 96],
    ['blues', '1 b3 4 b5 5 b7', 94, 'minor blues'],
    ['dorian', '1 2 b3 4 5 6 b7', 88],
    ['mixolydian', '1 2 3 4 5 6 b7', 88],
    ['harmonic minor', '1 2 b3 4 5 b6 7', 85],
    ['melodic minor', '1 2 b3 4 5 6 7', 82, 'jazz minor'],
    ['phrygian', '1 b2 b3 4 5 b6 b7', 80],
    ['lydian', '1 2 3 #4 5 6 7', 80],
    ['major blues', '1 2 b3 3 5 6', 78],
    ['phrygian dominant', '1 b2 3 4 5 b6 b7', 72, 'Spanish'],
    ['locrian', '1 b2 b3 4 b5 b6 b7', 70],
    ['lydian dominant', '1 2 3 #4 5 6 b7', 62],
    ['whole tone', '1 2 3 #4 #5 b7', 60],
    ['altered', '1 b2 #2 3 b5 #5 b7', 58, 'super Locrian'],
    ['diminished half-whole', '1 b2 #2 3 #4 5 6 b7', 58],
    ['diminished whole-half', '1 2 b3 4 b5 #5 6 7', 56],
    ['locrian ♮2', '1 2 b3 4 b5 b6 b7', 56, 'half-diminished'],
    ['hungarian minor', '1 2 b3 #4 5 b6 7', 54],
    ['double harmonic', '1 b2 3 4 5 b6 7', 54, 'Byzantine'],
    ['bebop dominant', '1 2 3 4 5 6 b7 7', 52],
    ['hirajoshi', '1 2 b3 5 b6', 52],
    ['bebop major', '1 2 3 4 5 #5 6 7', 50],
    ['in sen', '1 b2 4 5 b7', 50],
    ['iwato', '1 b2 4 b5 b7', 46],
    ['yo', '1 2 4 5 6', 46],
    ['neapolitan minor', '1 b2 b3 4 5 b6 7', 46],
    ['neapolitan major', '1 b2 b3 4 5 6 7', 44],
    ['persian', '1 b2 3 4 b5 b6 7', 44],
    ['enigmatic', '1 b2 3 #4 #5 #6 7', 40]
  ];

  var SCALES = SCALE_DEFS.map(function (d, i) {
    var degs = parseFormula(d[1]);
    var set = {};
    degs.forEach(function (g) { set[g.semis] = g; });
    return { id: i, name: d[0], degs: degs, w: d[2], aka: d[3] || '', set: set, size: degs.length };
  });

  function identifyScales(pcs, firstPc) {
    var out = [];
    for (var root = 0; root < 12; root++) {
      var rel = pcs.map(function (p) { return mod12(p - root); });
      SCALES.forEach(function (sc) {
        for (var i = 0; i < rel.length; i++) if (!sc.set[rel[i]]) return;
        var score = sc.w - (sc.size - pcs.length) * 2;
        if (root === firstPc) score += 22;
        else if (pcs.indexOf(root) >= 0) score += 6;
        out.push({ root: root, sc: sc, score: score });
      });
    }
    out.sort(function (a, b) { return b.score - a.score; });
    return out;
  }

  // Triads on each degree of a seven-note scale
  function keyChords(root, sc) {
    if (sc.size !== 7) return [];
    var pcs = sc.degs.map(function (d) { return mod12(root + d.semis); });
    // Chord roots take the scale's own spelling (F♯ major has E♯°, not F°)
    var names = spell(root, sc.degs).names;
    var roman = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
    var out = [];
    for (var i = 0; i < 7; i++) {
      var tri = [pcs[i], pcs[(i + 2) % 7], pcs[(i + 4) % 7]];
      var m = identifyChord(tri, tri[0]).filter(function (x) { return x.root === tri[0] && x.missing === 0; })[0];
      if (!m) return [];
      var nm = names[i] + m.type.sym;
      var r = roman[i];
      if (m.type.sym === 'm') r = r.toLowerCase();
      if (m.type.sym === 'dim') r = r.toLowerCase() + '°';
      if (m.type.sym === 'aug') r += '+';
      out.push({ name: nm.replace(/dim$/, '°'), roman: r });
    }
    return out;
  }

  /* ============================================================
     Fretboard (SVG). Vertical on phones like a chord chart,
     horizontal on wider screens like the neck in your hands.
  ============================================================ */

  var SVGNS = 'http://www.w3.org/2000/svg';
  var INLAYS = [3, 5, 7, 9, 15, 17, 19, 21];

  function svgEl(tag, attrs, text) {
    var e = document.createElementNS(SVGNS, tag);
    for (var k in attrs) e.setAttribute(k, attrs[k]);
    if (text != null) e.textContent = text;
    return e;
  }

  function Fretboard(host, opts) {
    this.host = host;
    this.opts = opts;
    this.tuning = opts.tuning;
    this.names = opts.names;
    this.marks = [];
    this.mutes = [];
    this.cursor = { s: 0, f: 0 };
    this.showCursor = false;
    this.svg = svgEl('svg', {
      role: 'application',
      tabindex: '0',
      'aria-label': opts.label || 'Guitar fretboard',
      'aria-describedby': opts.describedBy || ''
    });
    this.staticLayer = svgEl('g', {});
    this.markLayer = svgEl('g', {});
    this.svg.appendChild(this.staticLayer);
    this.svg.appendChild(this.markLayer);
    host.innerHTML = '';
    host.appendChild(this.svg);

    var self = this;
    this.svg.addEventListener('click', function (e) {
      var r = self.svg.getBoundingClientRect();
      var hit = self.locate(e.clientX - r.left, e.clientY - r.top);
      if (hit) { self.cursor = hit; self.opts.onTap(hit.s, hit.f); }
    });
    this.svg.addEventListener('keydown', function (e) { self.key(e); });
    this.svg.addEventListener('focus', function () {
      var kb = true;
      try { kb = self.svg.matches(':focus-visible'); } catch (err) { kb = true; }
      self.showCursor = kb;
      self.drawMarks();
    });
    this.svg.addEventListener('blur', function () { self.showCursor = false; self.drawMarks(); });

    this.width = 0;
    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        var w = Math.round(host.clientWidth);
        if (w && w !== self.width) self.layout();
      }).observe(host);
    } else {
      window.addEventListener('resize', function () { self.layout(); });
    }
    this.layout();
  }

  Fretboard.prototype.frets = function () {
    return this.vertical ? this.opts.fretsPhone : this.opts.frets;
  };

  Fretboard.prototype.layout = function () {
    var w = Math.round(this.host.clientWidth) || 340;
    this.width = w;
    // Same breakpoint as the CSS that reserves the board's height
    this.vertical = window.matchMedia ? !window.matchMedia('(min-width: 750px)').matches : w < 640;
    var F = this.frets();
    var g = {};
    if (this.vertical) {
      g.nameH = 26;
      g.openH = 42;
      g.top = g.nameH + g.openH;
      g.labW = 26;
      g.ss = (w - g.labW - 8) / 6;
      g.fh = 46;
      g.W = w;
      g.H = g.top + F * g.fh + 12;
    } else {
      g.numH = 22;
      g.nameW = 24;
      g.openW = 42;
      g.left = g.nameW + g.openW;
      g.ss = 34;
      g.fw = clamp((w - g.left - 12) / F, 34, 96);
      g.top = g.numH + 16;
      g.W = Math.min(w, g.left + F * g.fw + 12);
      g.H = g.top + 5 * g.ss + 16;
    }
    this.g = g;
    this.svg.setAttribute('viewBox', '0 0 ' + g.W + ' ' + g.H);
    this.svg.setAttribute('width', g.W);
    this.svg.setAttribute('height', g.H);
    this.svg.style.maxWidth = g.W + 'px';
    this.drawStatic();
    this.drawMarks();
  };

  // Centre of a note position
  Fretboard.prototype.pos = function (s, f) {
    var g = this.g;
    if (this.vertical) {
      return { x: g.labW + g.ss * (s + 0.5), y: f === 0 ? g.nameH + g.openH / 2 : g.top + (f - 0.5) * g.fh };
    }
    return { x: f === 0 ? g.nameW + g.openW / 2 : g.left + (f - 0.5) * g.fw, y: g.top + (5 - s) * g.ss };
  };

  Fretboard.prototype.locate = function (px, py) {
    var g = this.g;
    var F = this.frets();
    var s;
    var f;
    if (this.vertical) {
      s = Math.floor((px - g.labW) / g.ss);
      if (s < 0 || s > 5 || py < g.nameH - 6) return null;
      f = py < g.top ? 0 : Math.floor((py - g.top) / g.fh) + 1;
    } else {
      s = 5 - Math.round((py - g.top) / g.ss);
      if (s < 0 || s > 5 || px < g.nameW - 4) return null;
      f = px < g.left ? 0 : Math.floor((px - g.left) / g.fw) + 1;
    }
    if (f > F) return null;
    return { s: s, f: f };
  };

  Fretboard.prototype.drawStatic = function () {
    var L = this.staticLayer;
    var g = this.g;
    var F = this.frets();
    var V = this.vertical;
    var self = this;
    L.textContent = '';
    var boardLen = V ? F * g.fh : F * g.fw;
    var thick = [2.8, 2.3, 1.9, 1.5, 1.15, 0.9];

    if (V) {
      L.appendChild(svgEl('rect', { class: 'fb-wood', x: g.labW, y: g.top, width: g.ss * 6, height: boardLen, rx: 2 }));
      L.appendChild(svgEl('rect', { class: 'fb-nut', x: g.labW, y: g.top - 6, width: g.ss * 6, height: 7 }));
    } else {
      L.appendChild(svgEl('rect', { class: 'fb-wood', x: g.left, y: g.top - 14, width: boardLen, height: 5 * g.ss + 28, rx: 2 }));
      L.appendChild(svgEl('rect', { class: 'fb-nut', x: g.left - 6, y: g.top - 14, width: 7, height: 5 * g.ss + 28 }));
    }

    for (var f = 1; f <= F; f++) {
      if (V) {
        var y = g.top + f * g.fh;
        L.appendChild(svgEl('line', { class: 'fb-fret', x1: g.labW, x2: g.labW + g.ss * 6, y1: y, y2: y }));
      } else {
        var x = g.left + f * g.fw;
        L.appendChild(svgEl('line', { class: 'fb-fret', y1: g.top - 14, y2: g.top + 5 * g.ss + 14, x1: x, x2: x }));
      }
    }

    function inlay(f, off) {
      var c = V ? { x: g.labW + g.ss * 3 + off * g.ss * 1.5, y: g.top + (f - 0.5) * g.fh }
        : { x: g.left + (f - 0.5) * g.fw, y: g.top + 2.5 * g.ss + off * g.ss * 1.5 };
      L.appendChild(svgEl('circle', { class: 'fb-inlay', cx: c.x, cy: c.y, r: 5 }));
    }
    INLAYS.forEach(function (f) { if (f <= F) inlay(f, 0); });
    [12, 24].forEach(function (f) { if (f <= F) { inlay(f, -1); inlay(f, 1); } });

    for (var s = 0; s < 6; s++) {
      var muted = this.mutes.indexOf(s) >= 0;
      var p0 = this.pos(s, 1);
      var line = V
        ? svgEl('line', { class: 'fb-string' + (muted ? ' is-muted' : ''), x1: p0.x, x2: p0.x, y1: g.top - 6, y2: g.top + boardLen, 'stroke-width': thick[s] })
        : svgEl('line', { class: 'fb-string' + (muted ? ' is-muted' : ''), y1: p0.y, y2: p0.y, x1: g.left - 6, x2: g.left + boardLen, 'stroke-width': thick[s] });
      L.appendChild(line);
      var np = V ? { x: p0.x, y: g.nameH / 2 } : { x: g.nameW / 2, y: p0.y };
      L.appendChild(svgEl('text', { class: 'fb-name', x: np.x, y: np.y }, this.names[s]));
    }

    [1, 3, 5, 7, 9, 12, 15, 17, 19, 21, 24].forEach(function (f) {
      if (f > F) return;
      var p = V ? { x: g.labW / 2, y: g.top + (f - 0.5) * g.fh } : { x: g.left + (f - 0.5) * g.fw, y: g.numH / 2 };
      L.appendChild(svgEl('text', { class: 'fb-num', x: p.x, y: p.y }, String(f)));
    });
    self.staticKey = '';
  };

  // marks: [{ s, f, kind: 'user'|'fill'|'root', label, root: bool }]
  Fretboard.prototype.set = function (marks, mutes) {
    var mutesChanged = (mutes || []).join() !== this.mutes.join();
    this.marks = marks || [];
    this.mutes = mutes || [];
    if (mutesChanged) this.drawStatic();
    this.drawMarks();
  };

  Fretboard.prototype.drawMarks = function () {
    var L = this.markLayer;
    var g = this.g;
    var self = this;
    var prev = this._prev || {};
    var now = {};
    L.textContent = '';
    var cell = this.vertical ? Math.min(g.ss, g.fh) : Math.min(g.ss, g.fw);
    var r = clamp(cell * 0.38, 11, 17);
    this.marks.forEach(function (m) {
      var p = self.pos(m.s, m.f);
      var key = m.s + ':' + m.f + ':' + m.kind;
      now[key] = true;
      var grp = svgEl('g', { class: 'fb-mark fb-mark--' + m.kind + (m.root ? ' is-root' : '') + (prev[key] ? '' : ' is-new') });
      grp.appendChild(svgEl('circle', { cx: p.x, cy: p.y, r: r }));
      var label = String(m.label || '');
      grp.appendChild(svgEl('text', { x: p.x, y: p.y + 0.5, 'font-size': (label.length > 2 ? r * 0.78 : r * 0.95).toFixed(1) }, label));
      L.appendChild(grp);
    });
    this.mutes.forEach(function (s) {
      var p = self.pos(s, 0);
      var d = 7;
      L.appendChild(svgEl('path', { class: 'fb-mute', d: 'M' + (p.x - d) + ' ' + (p.y - d) + 'L' + (p.x + d) + ' ' + (p.y + d) + 'M' + (p.x + d) + ' ' + (p.y - d) + 'L' + (p.x - d) + ' ' + (p.y + d) }));
    });
    this._prev = now;
    if (this.showCursor) {
      var c = this.pos(this.cursor.s, this.cursor.f);
      var cw = this.vertical ? g.ss : (this.cursor.f === 0 ? g.openW : g.fw);
      var ch = this.vertical ? (this.cursor.f === 0 ? g.openH : g.fh) : g.ss;
      L.appendChild(svgEl('rect', { class: 'fb-cursor', x: c.x - cw / 2 + 2, y: c.y - ch / 2 + 2, width: cw - 4, height: ch - 4, rx: 3 }));
    }
  };

  Fretboard.prototype.key = function (e) {
    var c = this.cursor;
    var F = this.frets();
    var V = this.vertical;
    var k = e.key;
    var moved = true;
    if (k === 'ArrowUp') { if (V) c.f = Math.max(0, c.f - 1); else c.s = Math.min(5, c.s + 1); }
    else if (k === 'ArrowDown') { if (V) c.f = Math.min(F, c.f + 1); else c.s = Math.max(0, c.s - 1); }
    else if (k === 'ArrowLeft') { if (V) c.s = Math.max(0, c.s - 1); else c.f = Math.max(0, c.f - 1); }
    else if (k === 'ArrowRight') { if (V) c.s = Math.min(5, c.s + 1); else c.f = Math.min(F, c.f + 1); }
    else if (k === 'Enter' || k === ' ') { e.preventDefault(); this.opts.onTap(c.s, c.f); return; }
    else moved = false;
    if (moved) {
      e.preventDefault();
      this.showCursor = true;
      this.drawMarks();
      if (this.opts.onMove) this.opts.onMove(c.s, c.f);
    }
  };

  Fretboard.prototype.retune = function (tuning, names) {
    this.tuning = tuning;
    this.names = names;
    this.drawStatic();
    this.drawMarks();
  };

  function stringNames(key) {
    var flats = FLAT_TUNINGS[key];
    return TUNINGS[key].map(function (m) { return pcName(m, flats); });
  }

  function announce(el, text) { if (el) el.textContent = text; }

  // Shrink a one-line label until it fits its box instead of wrapping
  function fitOneLine(el) {
    if (!el) return;
    el.style.fontSize = '';
    var size = parseFloat(window.getComputedStyle(el).fontSize);
    var n = 0;
    while (el.scrollWidth > el.clientWidth + 1 && size > 15 && n++ < 40) {
      size -= 1.5;
      el.style.fontSize = size + 'px';
    }
  }

  /*
   * Every state of a readout uses the same fixed rows: the name on one line
   * (shrunk to fit, never wrapped), a line of text, the note chips and a
   * last line. Nothing in it changes height, so the fretboard under it never
   * moves while you tap. o: { name, line, notes, alt, empty, muted, tall, noAlt }
   */
  function paintReadout(out, o) {
    out.removeAttribute('data-pristine');
    out.innerHTML =
      '<div class="dt-readout__name' + (o.empty ? ' is-empty' : '') + '"><span>' + o.name + '</span></div>' +
      (o.tall
        ? '<p class="dt-readout__line is-tall is-muted">' + o.line + '</p>'
        : '<p class="dt-readout__line' + (o.muted ? ' is-muted' : '') + '">' + (o.line || '') + '</p>' +
          '<div class="dt-readout__notes">' + (o.notes || '') + '</div>' +
          (o.noAlt ? '' : '<p class="dt-readout__alt">' + (o.alt || '') + '</p>'));
    fitOneLine(out.querySelector('.dt-readout__name span'));
  }

  /* ============================================================
     CHORD IDENTIFIER
  ============================================================ */

  function Chords(root) {
    var $ = function (s) { return root.querySelector(s); };
    var out = $('[data-readout]');
    var suggest = $('[data-suggest]');
    var chips = $('[data-suggest-chips]');
    var suggestLabel = $('[data-suggest-label]');
    var playBtn = $('[data-play]');
    var clearBtn = $('[data-clear]');
    var tuningSel = $('[data-tuning]');
    var picker = $('[data-picker]');
    var rootsEl = $('[data-roots]');
    var typeSel = $('[data-type]');
    var pager = $('[data-pager]');
    var pagerLabel = $('[data-pager-label]');
    var live = $('[data-live]');
    var dockName = $('[data-dock-name]');
    var tabs = root.querySelectorAll('[data-tab]');

    var tuningKey = 'standard';
    var tuning = TUNINGS[tuningKey];
    var mode = 'name';
    var notes = [];
    var pick = null;
    var current = null;
    var build = { root: 9, sym: 'm', shapes: [], i: 0 };

    var board = new Fretboard($('[data-board]'), {
      tuning: tuning,
      names: stringNames(tuningKey),
      frets: 15,
      fretsPhone: 12,
      label: 'Fretboard. Tap a fret to place a note; tap above the nut to play a string open or mute it.',
      onTap: tap,
      onMove: function (s, f) { announce(live, ORDINAL[5 - s] + ' string, ' + (f ? 'fret ' + f : 'open')); }
    });

    function noteAt(s, f) { return tuning[s] + f; }

    function tap(s, f) {
      if (mode !== 'name') {
        // Tapping a built shape edits it: the shape becomes your notes
        var shown = build.shapes[build.i];
        notes = shown ? shown.shape.slice() : [];
        setMode('name');
      }
      if (f === 0) {
        notes[s] = notes[s] === undefined ? 0 : notes[s] === 0 ? 'x' : undefined;
      } else {
        notes[s] = notes[s] === f ? undefined : f;
      }
      pick = null;
      var n = notes[s];
      announce(live, n === undefined ? ORDINAL[5 - s] + ' string cleared' :
        n === 'x' ? ORDINAL[5 - s] + ' string muted' : pcName(noteAt(s, n)) + ' on the ' + ORDINAL[5 - s] + ' string');
      update();
    }

    function sounding() {
      var list = [];
      for (var s = 0; s < 6; s++) if (typeof notes[s] === 'number') list.push({ s: s, f: notes[s], midi: noteAt(s, notes[s]) });
      return list;
    }

    function notesHTML(rootPc, type, spelling, have) {
      return '<ul class="dt-notes">' + type.degs.map(function (d, i) {
        var pc = mod12(rootPc + d.semis);
        var missing = have && have.indexOf(pc) < 0;
        return '<li class="' + (d.degree === 1 ? 'is-root' : '') + (missing ? ' is-missing' : '') + '"><b>' + accHTML(spelling.names[i]) +
          '</b><span>' + (d.degree === 1 ? 'root' : d.label) + (missing ? ' · left out' : '') + '</span></li>';
      }).join('') + '</ul>';
    }

    function show(o) {
      paintReadout(out, o);
      dockName.innerHTML = o.empty ? '–' : o.name;
      fitOneLine(dockName);
    }

    function emptyReadout(title, hint) {
      // The page arrives with this empty state already drawn; redrawing it on
      // load made it the page's last big paint (a late LCP) for no change.
      if (out.hasAttribute('data-pristine')) return;
      show({ name: title, line: hint, empty: true, tall: true });
    }

    function showChips(label, list, onPick) {
      if (!list.length) { suggest.hidden = true; return; }
      suggest.hidden = false;
      suggestLabel.textContent = label;
      chips.innerHTML = list.map(function (c, i) {
        var nm = chordName(c.root, c.type, null);
        var on = pick && pick.root === c.root && pick.type === c.type;
        return '<button type="button" class="dt-chip" data-i="' + i + '" aria-pressed="' + (on ? 'true' : 'false') + '">' + accHTML(nm.name) +
          (c.need ? '<small>+' + c.need + '</small>' : '') + '</button>';
      }).join('');
      chips.onclick = function (e) {
        var b = e.target.closest('[data-i]');
        if (b) onPick(list[+b.getAttribute('data-i')]);
      };
    }

    function update() {
      if (mode === 'build') { renderBuild(); return; }
      var list = sounding();
      var mutes = [];
      for (var s = 0; s < 6; s++) if (notes[s] === 'x') mutes.push(s);
      current = null;

      if (!list.length) {
        board.set([], mutes);
        emptyReadout('Tap the fretboard', 'Place the notes of a chord you know, or one you stumbled on. One note per string; tap above the nut for an open string, tap again to mute it.');
        suggest.hidden = true;
        playBtn.disabled = true;
        return;
      }
      playBtn.disabled = false;
      var lowest = list.reduce(function (a, b) { return b.midi < a.midi ? b : a; });
      var pcs = [];
      list.forEach(function (n) { var pc = mod12(n.midi); if (pcs.indexOf(pc) < 0) pcs.push(pc); });
      var bass = mod12(lowest.midi);
      var matches = pcs.length >= 2 ? identifyChord(pcs, bass) : [];
      var fill = [];
      var spellMap = {};
      var rootPc = null;

      if (pick) {
        var shape = findShapes(pick.root, pick.type, tuning, { fixed: notes.slice(), maxFret: board.frets(), anyBass: true, limit: 1 })[0];
        var nm = chordName(pick.root, pick.type, null);
        nm.spelling.names.forEach(function (n, i) { spellMap[mod12(pick.root + pick.type.degs[i].semis)] = n; });
        rootPc = pick.root;
        if (shape) {
          for (s = 0; s < 6; s++) {
            if (notes[s] === undefined && shape.shape[s] !== 'x') fill.push({ s: s, f: shape.shape[s] });
            if (notes[s] === undefined && shape.shape[s] === 'x') mutes.push(s);
          }
          current = shape.shape.map(function (f, i) { return f === 'x' ? null : noteAt(i, f); }).filter(function (m) { return m != null; });
        }
        show({
          name: accHTML(nm.name),
          line: esc(nm.full),
          notes: notesHTML(pick.root, pick.type, nm.spelling, null),
          alt: shape ? 'Add the cream notes to finish the shape.' : 'No comfortable shape keeps your notes. Try moving one.'
        });
      } else if (matches.length) {
        var best = matches[0];
        var bn = chordName(best.root, best.type, best.bass);
        bn.spelling.names.forEach(function (n, i) { spellMap[mod12(best.root + best.type.degs[i].semis)] = n; });
        rootPc = best.root;
        var alts = [];
        matches.slice(1).forEach(function (m) {
          // Other names only when they use exactly these notes
          if (m.missing) return;
          var a = chordName(m.root, m.type, m.bass).name;
          if (alts.indexOf(a) < 0 && a !== bn.name && alts.length < 3) alts.push(a);
        });
        show({
          name: accHTML(bn.name),
          line: esc(bn.full),
          notes: notesHTML(best.root, best.type, bn.spelling, pcs),
          alt: alts.length ? 'Also called ' + alts.map(accHTML).join(', ') : ''
        });
        announce(live, 'Chord: ' + bn.full);
        current = list.map(function (n) { return n.midi; });
      } else {
        current = list.map(function (n) { return n.midi; });
        if (pcs.length === 1) {
          show({ name: accHTML(pcName(pcs[0])), line: 'One note so far. Add two more to name a chord.', muted: true });
        } else if (pcs.length === 2) {
          var lowPc = bass;
          var hiPc = pcs[0] === lowPc ? pcs[1] : pcs[0];
          var ivName = ['unison', 'minor second', 'major second', 'minor third', 'major third', 'perfect fourth', 'tritone', 'perfect fifth', 'minor sixth', 'major sixth', 'minor seventh', 'major seventh'][mod12(hiPc - lowPc)];
          show({ name: accHTML(pcName(lowPc)) + ' + ' + accHTML(pcName(hiPc)), line: 'A ' + ivName + '. Add one more note to make a chord.', muted: true });
        } else {
          show({ name: 'No standard chord', empty: true, line: 'These notes don’t spell a common chord. Try one of the chords below, or move a note.', muted: true });
        }
      }

      var marks = list.map(function (n) {
        var pc = mod12(n.midi);
        return { s: n.s, f: n.f, kind: 'user', label: spellMap[pc] || pcName(pc), root: pc === rootPc };
      });
      fill.forEach(function (n) {
        var pc = mod12(noteAt(n.s, n.f));
        marks.push({ s: n.s, f: n.f, kind: 'fill', label: spellMap[pc] || pcName(pc), root: false });
      });
      board.set(marks, mutes);

      if (pick || !matches.length || pcs.length <= 2) {
        showChips(pick ? 'Other chords with your notes' : 'Could become', chordCandidates(pcs, bass), function (c) {
          pick = pick && pick.root === c.root && pick.type === c.type ? null : c;
          update();
        });
      } else {
        suggest.hidden = true;
      }
    }

    /* Build a chord */
    function renderRoots() {
      rootsEl.innerHTML = PC_NAME.map(function (n, pc) {
        return '<button type="button" class="dt-chip" data-root="' + pc + '" aria-pressed="' + (pc === build.root ? 'true' : 'false') + '">' + accHTML(n) + '</button>';
      }).join('');
    }

    function computeShapes() {
      var type = chordBySym(build.sym);
      build.shapes = findShapes(build.root, type, tuning, { spread: true, limit: 6, maxFret: board.frets() });
      build.i = 0;
    }

    function renderBuild() {
      var type = chordBySym(build.sym);
      var nm = chordName(build.root, type, null);
      var shape = build.shapes[build.i];
      var spellMap = {};
      nm.spelling.names.forEach(function (n, i) { spellMap[mod12(build.root + type.degs[i].semis)] = n; });
      var marks = [];
      var mutes = [];
      current = null;
      if (shape) {
        shape.shape.forEach(function (f, s) {
          if (f === 'x') { mutes.push(s); return; }
          var pc = mod12(noteAt(s, f));
          marks.push({ s: s, f: f, kind: 'user', label: spellMap[pc] || pcName(pc), root: pc === build.root });
        });
        current = shape.shape.map(function (f, s) { return f === 'x' ? null : noteAt(s, f); }).filter(function (m) { return m != null; });
      }
      // A shape higher up than the phone board shows starts the board there
      board.set(marks.filter(function (m) { return m.f <= board.frets(); }), mutes);
      // Always there in build mode (never hidden), so nothing above the board changes height
      var steps = root.querySelectorAll('[data-step]');
      for (var k = 0; k < steps.length; k++) steps[k].disabled = build.shapes.length < 2;
      var openPos = shape && (shape.pos === 0 || (shape.pos <= 3 && shape.shape.indexOf(0) >= 0));
      pagerLabel.textContent = build.shapes.length ? 'Shape ' + (build.i + 1) + ' of ' + build.shapes.length + (openPos ? ' · open position' : ' · fret ' + shape.pos) : 'No shapes';
      show({
        name: accHTML(nm.name),
        line: esc(nm.full),
        notes: notesHTML(build.root, type, nm.spelling, null),
        alt: shape ? '' : 'No playable shape in this tuning.'
      });
      playBtn.disabled = !shape;
      suggest.hidden = true;
      announce(live, nm.full + (shape ? ', shape ' + (build.i + 1) + ' of ' + build.shapes.length : ''));
    }

    function setMode(m) {
      mode = m;
      for (var i = 0; i < tabs.length; i++) tabs[i].setAttribute('aria-selected', tabs[i].getAttribute('data-tab') === m ? 'true' : 'false');
      picker.hidden = m !== 'build';
      if (m === 'build') { renderRoots(); computeShapes(); }
      update();
    }

    for (var i = 0; i < tabs.length; i++) {
      tabs[i].addEventListener('click', function () { setMode(this.getAttribute('data-tab')); });
    }

    rootsEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-root]');
      if (!b) return;
      build.root = +b.getAttribute('data-root');
      renderRoots();
      computeShapes();
      renderBuild();
    });

    typeSel.innerHTML = BUILD_TYPES.map(function (sym) {
      var t = chordBySym(sym);
      return '<option value="' + esc(sym) + '"' + (sym === build.sym ? ' selected' : '') + '>' + esc((sym || 'maj') + ' · ' + t.name) + '</option>';
    }).join('');
    typeSel.addEventListener('change', function () { build.sym = typeSel.value; computeShapes(); renderBuild(); });

    root.addEventListener('click', function (e) {
      var b = e.target.closest('[data-step]');
      if (!b || !build.shapes.length) return;
      build.i = (build.i + +b.getAttribute('data-step') + build.shapes.length) % build.shapes.length;
      renderBuild();
    });

    playBtn.addEventListener('click', function () {
      if (!current || !current.length) return;
      Sound.strum(current.slice().sort(function (a, b) { return a - b; }).map(function (m) { return freqOf(m); }));
    });

    clearBtn.addEventListener('click', function () {
      notes = [];
      pick = null;
      if (mode === 'build') setMode('name');
      else update();
      announce(live, 'Fretboard cleared');
    });

    tuningSel.addEventListener('change', function () {
      tuningKey = TUNINGS[tuningSel.value] ? tuningSel.value : 'standard';
      tuning = TUNINGS[tuningKey];
      board.retune(tuning, stringNames(tuningKey));
      if (mode === 'build') computeShapes();
      update();
    });

    update();
  }

  /* ============================================================
     SCALE IDENTIFIER
  ============================================================ */

  function Scales(root) {
    var $ = function (s) { return root.querySelector(s); };
    var out = $('[data-readout]');
    var resultsWrap = $('[data-results-wrap]');
    var resultsEl = $('[data-results]');
    var moreBtn = $('[data-more]');
    var playBtn = $('[data-play]');
    var clearBtn = $('[data-clear]');
    var tuningSel = $('[data-tuning]');
    var picker = $('[data-picker]');
    var rootSel = $('[data-scale-root]');
    var scaleSel = $('[data-scale-type]');
    var degToggle = $('[data-degrees]');
    var live = $('[data-live]');
    var dockName = $('[data-dock-name]');
    var keyWrap = $('[data-keychords]');
    var keyList = $('[data-keychords-list]');
    var tabs = root.querySelectorAll('[data-tab]');

    var tuningKey = 'standard';
    var tuning = TUNINGS[tuningKey];
    var mode = 'find';
    var taps = [];
    var chosen = null;
    var userChose = false;
    var showAll = false;
    var results = [];

    var board = new Fretboard($('[data-board]'), {
      tuning: tuning,
      names: stringNames(tuningKey),
      frets: 15,
      fretsPhone: 12,
      label: 'Fretboard. Tap frets to add the notes you know.',
      onTap: tap,
      onMove: function (s, f) { announce(live, ORDINAL[5 - s] + ' string, ' + (f ? 'fret ' + f : 'open')); }
    });

    function noteAt(s, f) { return tuning[s] + f; }

    function tap(s, f) {
      if (mode !== 'find') setMode('find');
      var k = s + ':' + f;
      var at = -1;
      for (var i = 0; i < taps.length; i++) if (taps[i].k === k) at = i;
      if (at >= 0) taps.splice(at, 1);
      else taps.push({ k: k, s: s, f: f });
      announce(live, (at >= 0 ? 'Removed ' : 'Added ') + pcName(noteAt(s, f)));
      if (!chosen || !userChose || !fits(chosen)) { chosen = null; userChose = false; }
      showAll = false;
      update();
    }

    function pcsOf() {
      var pcs = [];
      taps.forEach(function (t) { var pc = mod12(noteAt(t.s, t.f)); if (pcs.indexOf(pc) < 0) pcs.push(pc); });
      return pcs;
    }

    function fits(c) {
      return pcsOf().every(function (pc) { return c.sc.set[mod12(pc - c.root)]; });
    }

    function scaleTitle(c) {
      var sp = spell(c.root, c.sc.degs);
      return { title: sp.root + ' ' + c.sc.name, sp: sp };
    }

    function renderResults() {
      if (mode !== 'find' || pcsOf().length < 2) { resultsWrap.hidden = true; return; }
      resultsWrap.hidden = false;
      var list = showAll ? results.slice(0, 40) : results.slice(0, 8);
      resultsEl.innerHTML = list.map(function (c, i) {
        var t = scaleTitle(c);
        var on = chosen && chosen.root === c.root && chosen.sc === c.sc;
        return '<li><button type="button" class="dt-result" data-r="' + i + '" aria-pressed="' + (on ? 'true' : 'false') + '"><b>' +
          accHTML(t.title) + '</b><span>' + t.sp.names.map(accHTML).join(' ') + '</span></button></li>';
      }).join('');
      moreBtn.hidden = showAll || results.length <= 8;
      moreBtn.textContent = 'Show all ' + Math.min(results.length, 40) + ' matches';
    }

    function update() {
      var pcs = pcsOf();
      if (mode === 'find') {
        results = pcs.length >= 2 ? identifyScales(pcs, taps.length ? mod12(noteAt(taps[0].s, taps[0].f)) : null) : [];
        if (!chosen && results.length) chosen = results[0];
        if (pcs.length < 2) chosen = null;
      }
      renderResults();
      draw();
    }

    function draw() {
      var labels = degToggle && degToggle.checked ? 'deg' : 'name';
      var marks = [];
      var F = board.frets();
      var userKeys = {};
      taps.forEach(function (t) { userKeys[t.k] = true; });
      var spellMap = {};
      var degMap = {};
      if (chosen) {
        var t = scaleTitle(chosen);
        t.sp.names.forEach(function (n, i) {
          var pc = mod12(chosen.root + chosen.sc.degs[i].semis);
          spellMap[pc] = n;
          degMap[pc] = chosen.sc.degs[i].label;
        });
        for (var s = 0; s < 6; s++) {
          for (var f = 0; f <= F; f++) {
            var pc = mod12(noteAt(s, f));
            if (spellMap[pc] === undefined) continue;
            var isUser = userKeys[s + ':' + f];
            var isRoot = pc === chosen.root;
            marks.push({
              s: s, f: f,
              kind: isUser ? 'user' : isRoot ? 'root' : 'fill',
              label: labels === 'deg' ? (isRoot ? 'R' : degMap[pc]) : spellMap[pc],
              root: isRoot
            });
          }
        }
        var kc = keyChords(chosen.root, chosen.sc);
        paintReadout(out, {
          name: accHTML(t.title),
          line: (chosen.sc.aka ? 'Also called ' + esc(chosen.sc.aka) + ' · ' : '') + chosen.sc.size + ' notes',
          muted: true,
          noAlt: true,
          notes: '<ul class="dt-notes">' + chosen.sc.degs.map(function (d, i) {
            return '<li class="' + (d.degree === 1 ? 'is-root' : '') + '"><b>' + accHTML(t.sp.names[i]) + '</b><span>' + (d.degree === 1 ? 'root' : d.label) + '</span></li>';
          }).join('') + '</ul>'
        });
        // Chords in the key sit under the board, where showing or hiding them moves nothing you're tapping
        keyWrap.hidden = !kc.length;
        keyList.innerHTML = kc.map(function (c) {
          return '<li><b>' + accHTML(c.name) + '</b><span>' + c.roman + '</span></li>';
        }).join('');
        playBtn.disabled = false;
        dockName.innerHTML = accHTML(t.title);
        fitOneLine(dockName);
        announce(live, t.title + ': ' + t.sp.names.join(' '));
      } else {
        taps.forEach(function (tp) {
          var pc = mod12(noteAt(tp.s, tp.f));
          marks.push({ s: tp.s, f: tp.f, kind: 'user', label: pcName(pc), root: false });
        });
        var n = pcsOf().length;
        keyWrap.hidden = true;
        playBtn.disabled = true;
        dockName.textContent = '–';
        if (!(!n && out.hasAttribute('data-pristine'))) {
          paintReadout(out, {
            name: n ? 'Add another note' : 'Tap the notes you know',
            empty: true,
            tall: true,
            noAlt: true,
            line: n ? 'Scales start to show from two different notes. The first note you tap counts as the likely root.'
              : 'Tap a few notes from a riff or solo and every scale that holds them shows up. Or switch to “Show a scale” and pick one.'
          });
        }
      }
      board.set(marks.filter(function (m) { return m.f <= F; }), []);
    }

    function setMode(m) {
      mode = m;
      for (var i = 0; i < tabs.length; i++) tabs[i].setAttribute('aria-selected', tabs[i].getAttribute('data-tab') === m ? 'true' : 'false');
      picker.hidden = m !== 'show';
      if (m === 'show') {
        chosen = { root: +rootSel.value, sc: SCALES[+scaleSel.value] };
        userChose = true;
      } else {
        chosen = null;
        userChose = false;
      }
      update();
    }

    rootSel.innerHTML = PC_NAME.map(function (n, pc) { return '<option value="' + pc + '"' + (pc === 9 ? ' selected' : '') + '>' + n + '</option>'; }).join('');
    scaleSel.innerHTML = SCALES.map(function (sc, i) { return '<option value="' + i + '"' + (sc.name === 'minor pentatonic' ? ' selected' : '') + '>' + sc.name + '</option>'; }).join('');

    function fromPicker() { chosen = { root: +rootSel.value, sc: SCALES[+scaleSel.value] }; userChose = true; update(); }
    rootSel.addEventListener('change', fromPicker);
    scaleSel.addEventListener('change', fromPicker);

    for (var i = 0; i < tabs.length; i++) {
      tabs[i].addEventListener('click', function () { setMode(this.getAttribute('data-tab')); });
    }

    resultsEl.addEventListener('click', function (e) {
      var b = e.target.closest('[data-r]');
      if (!b) return;
      chosen = results[+b.getAttribute('data-r')];
      userChose = true;
      renderResults();
      draw();
    });

    moreBtn.addEventListener('click', function () { showAll = true; renderResults(); });
    if (degToggle) degToggle.addEventListener('change', draw);

    playBtn.addEventListener('click', function () {
      if (!chosen) return;
      // One octave up from the lowest root on the neck
      var base = tuning[0] + mod12(chosen.root - tuning[0]);
      var run = chosen.sc.degs.map(function (d) { return base + d.semis; }).sort(function (a, b) { return a - b; });
      run.push(base + 12);
      Sound.run(run.map(function (m) { return freqOf(m); }), 0.26);
    });

    clearBtn.addEventListener('click', function () {
      taps = [];
      chosen = null;
      userChose = false;
      if (mode === 'show') setMode('find');
      else update();
      announce(live, 'Fretboard cleared');
    });

    tuningSel.addEventListener('change', function () {
      tuningKey = TUNINGS[tuningSel.value] ? tuningSel.value : 'standard';
      tuning = TUNINGS[tuningKey];
      taps = [];
      if (mode === 'find') chosen = null;
      board.retune(tuning, stringNames(tuningKey));
      update();
    });

    update();
  }

  /* ============================================================
     Boot
  ============================================================ */

  var TOOLS = { tuner: Tuner, chords: Chords, scales: Scales };

  function boot(scope) {
    var nodes = (scope || document).querySelectorAll('[data-dorti-tool]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var kind = el.getAttribute('data-dorti-tool');
      if (el.__dortiTool || !TOOLS[kind]) continue;
      el.__dortiTool = true;
      try { TOOLS[kind](el); } catch (err) { if (window.console) console.error('DORTI tool failed to start', err); }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  // Theme editor: sections re-render in place
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

  // For tests in the console: window.DortiTheory
  window.DortiTheory = {
    identifyChord: identifyChord,
    chordName: chordName,
    findShapes: findShapes,
    identifyScales: identifyScales,
    keyChords: keyChords,
    chordBySym: chordBySym,
    SCALES: SCALES,
    TUNINGS: TUNINGS,
    PitchDetector: PitchDetector
  };
})();
